/**
 * Opaque value the backend substitutes for another account's GUID.
 * Must match EmailLookup.RedactedId in Phoenix_Back_End (Helper/Security/EmailLookup.cs).
 */
export const REDACTED_ID = "__redacted__";

/**
 * Interprets the response from POST patients/check_duplicate_email.
 *
 * S3DEV2-7649 (security audit AZ-A-06): that endpoint used to be reachable with no credentials and
 * returned the account GUID of every login holding the address, which let an anonymous caller turn
 * a known email into a staff GUID. It now requires a token and answers with `in_use`, never with
 * somebody else's GUID.
 *
 * `in_use` is authoritative — the backend does the comparison against `user_id` itself. The `ids`
 * branch below is only for a backend that predates the change: during a deploy where the frontend
 * is ahead, `in_use` would be undefined, and treating that as "address is free" would silently
 * stop warning about duplicates. Because sEmail is the global login key, a duplicate prevents the
 * losing account from signing in, so the fallback keeps the warning working across that window.
 *
 * Be clear about how much this is worth: the duplicate check is advisory. Nothing on the server
 * rejects a duplicate address (PatientsSerializer.SetEmail and UsersSerializer's "email" case both
 * assign without checking), so any caller that ignores this validation can already create one.
 * This fallback preserves a best-effort UX guard at its current strength; it is not, and never
 * was, the thing that keeps addresses unique.
 *
 * TODO (follow-up, once sprint 259 is deployed everywhere): delete the `ids` fallback here and the
 * `ids` field from the backend response. Both halves have to go in the same change.
 *
 * @param {{ in_use?: boolean, ids?: string[] }} response
 * @param {string} [userId] GUID of the record being edited; absent when creating.
 * @returns {boolean} true when another account already holds the address.
 */
export const isEmailTaken = (response = {}, userId) => {
  const { in_use, ids } = response;

  if (typeof in_use === "boolean") return in_use;

  // Legacy shape, only reached against a backend that predates the change.
  if (!Array.isArray(ids) || ids.length === 0) return false;

  // A match only means "that's me" when it is a GUID the caller supplied. The sentinel is never
  // the caller, even if user_id somehow arrives as the sentinel string itself — treating it as a
  // self-match would report a taken address as free.
  const matchesCaller = ids.some(id => id === userId && id !== REDACTED_ID);

  // `userId` is undefined on the create path, which matches nothing — so any id means taken.
  return !matchesCaller;
};

export default isEmailTaken;
